import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

// Default inline style used for the trigger button. Separated so it can be
// merged with user provided `buttonStyle` prop.
const DEFAULT_BUTTON_STYLE = {
  backgroundImage: `linear-gradient(180deg, ${colorHelpers.withOpacity(UI_COLORS.FORMS.INPUT.background, 1)} 0%, ${colorHelpers.withOpacity(UI_COLORS.FORMS.INPUT.background, 0.98)} 100%)`,
  borderColor: UI_COLORS.FORMS.INPUT.border,
  color: UI_COLORS.FORMS.INPUT.text,
};

/**
 * DropBox
 *
 * A lightweight custom select/dropdown component. It supports keyboard and
 * mouse interactions, custom renderers for labels, custom option styles and
 * optional icons. The component manages its own open state and notifies the
 * parent via `onChange`, `onOpen` and `onClose` callbacks.
 *
 * Props:
 * - options: array of { value, label, icon }
 * - value: currently selected value
 * - onChange(value, option): called when user selects an option
 * - placeholder: text when nothing is selected
 * - align: 'left' | 'right' alignment of dropdown menu
 * - fullWidth: whether trigger expands to full width
 * - renderLabel: optional function to render the displayed label
 */
const DropBox = ({
  options = [],
  value,
  onChange,
  placeholder,
  disabled = false,
  buttonClassName = '',
  buttonStyle = {},
  dropdownClassName = '',
  dropdownStyle = {},
  optionClassName = '',
  getOptionStyle,
  align = 'left',
  fullWidth = true,
  showCheckmark = true,
  chevronIcon = faChevronDown,
  checkIcon = faCheck,
  renderLabel,
  onOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder ?? t('common.formFields.placeholders.selectOption');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const computedOptions = useMemo(
    () => options.map((option) => ({
      ...option,
      value: option.value,
      label: option.label ?? option.value,
    })),
    [options]
  );

  const selectedOption = useMemo(
    () => computedOptions.find((option) => option.value === value),
    [computedOptions, value]
  );

  const displayLabel = useMemo(() => {
    if (typeof renderLabel === 'function') {
      return renderLabel(selectedOption);
    }
    return selectedOption?.label ?? effectivePlaceholder;
  }, [effectivePlaceholder, renderLabel, selectedOption]);

  useEffect(() => {
    if (!open) return undefined;

    // Close the dropdown when user clicks outside the container
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        onClose?.();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        onOpen?.();
      } else {
        onClose?.();
      }
      return next;
    });
  };

  const handleSelect = (option) => {
    if (disabled) return;
    onChange?.(option.value, option);
    setOpen(false);
    onClose?.();
  };

  const dropdownAlignmentClass = align === 'right' ? 'right-0' : 'left-0';
  const containerClass = fullWidth ? 'w-full' : 'inline-block';

  return (
    <div className={`relative ${containerClass}`} ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        className={`flex w-full items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3 text-sm font-medium transition-all duration-200 shadow-sm focus:outline-none ${buttonClassName}`}
        style={{
          ...DEFAULT_BUTTON_STYLE,
          ...buttonStyle,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.7 : 1,
          boxShadow: open
            ? '0 10px 30px rgba(15,23,42,0.08)'
            : '0 6px 18px rgba(15,23,42,0.06)',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="truncate">{displayLabel}</span>
        {chevronIcon && (
          <FontAwesomeIcon icon={chevronIcon} className="ml-2 text-sm" />
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          aria-activedescendant={selectedOption?.value}
          tabIndex={-1}
          className={`absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-xl border shadow-lg ${dropdownAlignmentClass} ${dropdownClassName}`}
          style={{
            borderColor: UI_COLORS.BORDERS.MEDIUM,
            background: UI_COLORS.BACKGROUNDS.SECTION || '#fff',
            ...dropdownStyle,
          }}
        >
          {computedOptions.map((option) => {
            const isSelected = option.value === value;
            const optionStyles = {
              color: UI_COLORS.FORMS.INPUT.text,
              borderBottom: '1px solid rgba(0,0,0,0.03)',
              ...((typeof getOptionStyle === 'function'
                ? getOptionStyle(option, isSelected)
                : {}) || {}),
            };

            return (
              <li
                key={option.value}
                id={option.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSelect(option);
                  }
                }}
                className={`flex cursor-pointer items-center justify-between px-4 py-3 text-sm transition-colors duration-150 hover:bg-gray-50 ${
                  isSelected ? 'bg-gray-100' : ''
                } ${optionClassName}`}
                style={optionStyles}
              >
                <span className="flex items-center gap-2">
                  {option.icon && (
                    <FontAwesomeIcon icon={option.icon} className="text-xs" />
                  )}
                  <span className="truncate">{option.label}</span>
                </span>
                {showCheckmark && isSelected && checkIcon && (
                  <FontAwesomeIcon icon={checkIcon} className="text-sm" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

DropBox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttonStyle: PropTypes.object,
  dropdownClassName: PropTypes.string,
  dropdownStyle: PropTypes.object,
  optionClassName: PropTypes.string,
  getOptionStyle: PropTypes.func,
  align: PropTypes.oneOf(['left', 'right']),
  fullWidth: PropTypes.bool,
  showCheckmark: PropTypes.bool,
  chevronIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  checkIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  renderLabel: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default DropBox;
